<template>
  <div class="role-management">
    <!-- 页面标题 -->
    <div class="page-header">
      <h1>角色管理</h1>
      <div class="header-actions">
        <el-button type="primary" @click="handleCreateRole">
          <el-icon><Plus /></el-icon>
          新增角色
        </el-button>
        <el-button @click="refreshData">
          <el-icon><Refresh /></el-icon>
          刷新
        </el-button>
      </div>
    </div>

    <!-- 搜索和过滤 -->
    <el-card class="filter-card">
      <el-form :model="searchForm" inline>
        <el-form-item label="角色名称">
          <el-input
            v-model="searchForm.name"
            placeholder="请输入角色名称"
            clearable
            style="width: 200px"
          />
        </el-form-item>
        <el-form-item label="角色编码">
          <el-input
            v-model="searchForm.code"
            placeholder="请输入角色编码"
            clearable
            style="width: 200px"
          />
        </el-form-item>
        <el-form-item label="状态">
          <el-select
            v-model="searchForm.status"
            placeholder="请选择状态"
            clearable
            style="width: 120px"
          >
            <el-option label="启用" value="active" />
            <el-option label="禁用" value="inactive" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">
            <el-icon><Search /></el-icon>
            搜索
          </el-button>
          <el-button @click="handleReset">
            <el-icon><Refresh /></el-icon>
            重置
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 角色列表 -->
    <el-card class="table-card">
      <el-table
        :data="filteredRoles"
        v-loading="loading"
        stripe
        style="width: 100%"
      >
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="name" label="角色名称" width="150" />
        <el-table-column prop="code" label="角色编码" width="120" />
        <el-table-column prop="description" label="描述" min-width="200" />
        <el-table-column prop="permissions" label="权限数量" width="100">
          <template #default="{ row }">
            <el-tag size="small" type="info">
              {{ row.permissions.length }}个
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template #default="{ row }">
            <el-tag
              :type="row.status === 'active' ? 'success' : 'danger'"
              size="small"
            >
              {{ row.status === 'active' ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="创建时间" width="180">
          <template #default="{ row }">
            {{ formatDate(row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="{ row }">
            <el-button
              type="primary"
              size="small"
              @click="handleEditRole(row)"
              v-permission="'permission:edit'"
            >
              编辑
            </el-button>
            <el-button
              type="info"
              size="small"
              @click="handleViewPermissions(row)"
            >
              权限
            </el-button>
            <el-popconfirm
              title="确定要删除这个角色吗？"
              confirm-button-text="确定"
              cancel-button-text="取消"
              @confirm="handleDeleteRole(row.id)"
              v-permission="'permission:edit'"
            >
              <template #reference>
                <el-button type="danger" size="small">删除</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 创建/编辑角色对话框 -->
    <el-dialog
      v-model="roleDialogVisible"
      :title="roleDialogTitle"
      width="600px"
      :close-on-click-modal="false"
    >
      <el-form
        ref="roleFormRef"
        :model="roleForm"
        :rules="roleRules"
        label-width="100px"
      >
        <el-form-item label="角色名称" prop="name">
          <el-input
            v-model="roleForm.name"
            placeholder="请输入角色名称"
            maxlength="50"
          />
        </el-form-item>
        <el-form-item label="角色编码" prop="code">
          <el-input
            v-model="roleForm.code"
            placeholder="请输入角色编码"
            maxlength="50"
          />
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input
            v-model="roleForm.description"
            type="textarea"
            :rows="3"
            placeholder="请输入角色描述"
            maxlength="200"
            show-word-limit
          />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="roleForm.status">
            <el-radio label="active">启用</el-radio>
            <el-radio label="inactive">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="权限分配" prop="permissions">
          <el-checkbox-group v-model="roleForm.permissions">
            <div class="permission-groups">
              <div
                v-for="module in permissionGroups"
                :key="module.name"
                class="permission-group"
              >
                <div class="module-title">{{ module.name }}</div>
                <div class="permission-list">
                  <el-checkbox
                    v-for="permission in module.permissions"
                    :key="permission.id"
                    :label="permission.code"
                    :value="permission.code"
                  >
                    {{ permission.name }}
                    <span class="permission-desc">({{ permission.description }})</span>
                  </el-checkbox>
                </div>
              </div>
            </div>
          </el-checkbox-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="roleDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmitRole" :loading="submitting">
            确定
          </el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 查看权限对话框 -->
    <el-dialog
      v-model="permissionDialogVisible"
      title="角色权限详情"
      width="800px"
    >
      <div class="permission-detail">
        <div class="role-info">
          <h3>{{ currentRole?.name }} ({{ currentRole?.code }})</h3>
          <p>{{ currentRole?.description }}</p>
        </div>
        <div class="permission-list">
          <h4>拥有的权限：</h4>
          <el-table :data="rolePermissions" stripe>
            <el-table-column prop="module" label="模块" width="120" />
            <el-table-column prop="name" label="权限名称" width="150" />
            <el-table-column prop="code" label="权限编码" width="150" />
            <el-table-column prop="description" label="描述" />
          </el-table>
        </div>
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="permissionDialogVisible = false">关闭</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, nextTick } from 'vue'
import { useStore } from 'vuex'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Refresh, Search } from '@element-plus/icons-vue'

const store = useStore()

// 状态
const loading = ref(false)
const submitting = ref(false)
const roleDialogVisible = ref(false)
const permissionDialogVisible = ref(false)
const currentRole = ref(null)

// 搜索表单
const searchForm = reactive({
  name: '',
  code: '',
  status: ''
})

// 角色表单
const roleFormRef = ref(null)
const roleForm = reactive({
  id: null,
  name: '',
  code: '',
  description: '',
  status: 'active',
  permissions: []
})

// 表单验证规则
const roleRules = {
  name: [
    { required: true, message: '请输入角色名称', trigger: 'blur' },
    { min: 2, max: 50, message: '长度在2到50个字符', trigger: 'blur' }
  ],
  code: [
    { required: true, message: '请输入角色编码', trigger: 'blur' },
    { min: 2, max: 50, message: '长度在2到50个字符', trigger: 'blur' }
  ],
  description: [
    { required: true, message: '请输入角色描述', trigger: 'blur' },
    { min: 5, max: 200, message: '长度在5到200个字符', trigger: 'blur' }
  ],
  status: [
    { required: true, message: '请选择状态', trigger: 'change' }
  ]
}

// 计算属性
const roles = computed(() => store.getters['permission/roles'])
const permissions = computed(() => store.getters['permission/permissions'])

// 按模块分组的权限
const permissionGroups = computed(() => {
  const groups = {}
  permissions.value.forEach(permission => {
    if (!groups[permission.module]) {
      groups[permission.module] = {
        name: permission.module,
        permissions: []
      }
    }
    groups[permission.module].permissions.push(permission)
  })
  return Object.values(groups)
})

// 过滤后的角色列表
const filteredRoles = computed(() => {
  let filtered = roles.value
  
  if (searchForm.name) {
    filtered = filtered.filter(role => 
      role.name.toLowerCase().includes(searchForm.name.toLowerCase())
    )
  }
  
  if (searchForm.code) {
    filtered = filtered.filter(role => 
      role.code.toLowerCase().includes(searchForm.code.toLowerCase())
    )
  }
  
  if (searchForm.status) {
    filtered = filtered.filter(role => role.status === searchForm.status)
  }
  
  return filtered
})

// 当前角色的权限详情
const rolePermissions = computed(() => {
  if (!currentRole.value) return []
  
  const role = currentRole.value
  if (role.permissions.includes('*')) {
    // 拥有所有权限
    return permissions.value
  }
  
  return permissions.value.filter(permission => 
    role.permissions.includes(permission.code)
  )
})

// 对话框标题
const roleDialogTitle = computed(() => {
  return roleForm.id ? '编辑角色' : '新增角色'
})

// 方法
const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN')
}

// 加载数据
const loadData = async () => {
  loading.value = true
  try {
    await Promise.all([
      store.dispatch('permission/fetchRoles'),
      store.dispatch('permission/fetchPermissions')
    ])
  } catch (error) {
    ElMessage.error('加载数据失败：' + error.message)
  } finally {
    loading.value = false
  }
}

// 刷新数据
const refreshData = () => {
  loadData()
}

// 搜索
const handleSearch = () => {
  // 搜索逻辑已经在computed中实现
}

// 重置搜索
const handleReset = () => {
  searchForm.name = ''
  searchForm.code = ''
  searchForm.status = ''
}

// 创建角色
const handleCreateRole = () => {
  Object.assign(roleForm, {
    id: null,
    name: '',
    code: '',
    description: '',
    status: 'active',
    permissions: []
  })
  roleDialogVisible.value = true
  nextTick(() => {
    if (roleFormRef.value) {
      roleFormRef.value.clearValidate()
    }
  })
}

// 编辑角色
const handleEditRole = (role) => {
  Object.assign(roleForm, {
    id: role.id,
    name: role.name,
    code: role.code,
    description: role.description,
    status: role.status,
    permissions: [...role.permissions]
  })
  roleDialogVisible.value = true
  nextTick(() => {
    if (roleFormRef.value) {
      roleFormRef.value.clearValidate()
    }
  })
}

// 查看权限
const handleViewPermissions = (role) => {
  currentRole.value = role
  permissionDialogVisible.value = true
}

// 删除角色
const handleDeleteRole = async (id) => {
  try {
    // 检查是否是超级管理员角色
    const role = roles.value.find(r => r.id === id)
    if (role?.code === 'admin') {
      ElMessage.warning('不能删除超级管理员角色')
      return
    }
    
    await store.dispatch('permission/deleteRole', id)
    ElMessage.success('删除成功')
    
    // 记录操作日志
    await store.dispatch('operationLog/addLog', {
      adminId: store.getters['auth/adminInfo'].id,
      adminName: store.getters['auth/adminInfo'].name,
      action: '删除角色',
      module: '权限管理',
      target: `角色ID: ${id}`,
      details: `删除了角色: ${role?.name}`,
      ip: '192.168.1.100',
      status: 'success'
    })
  } catch (error) {
    ElMessage.error('删除失败：' + error.message)
  }
}

// 提交角色表单
const handleSubmitRole = async () => {
  if (!roleFormRef.value) return
  
  try {
    await roleFormRef.value.validate()
    submitting.value = true
    
    if (roleForm.id) {
      // 更新角色
      await store.dispatch('permission/updateRole', roleForm)
      ElMessage.success('更新成功')
      
      // 记录操作日志
      await store.dispatch('operationLog/addLog', {
        adminId: store.getters['auth/adminInfo'].id,
        adminName: store.getters['auth/adminInfo'].name,
        action: '更新角色',
        module: '权限管理',
        target: `角色ID: ${roleForm.id}`,
        details: `更新了角色: ${roleForm.name}`,
        ip: '192.168.1.100',
        status: 'success'
      })
    } else {
      // 创建角色
      const newRole = await store.dispatch('permission/createRole', roleForm)
      ElMessage.success('创建成功')
      
      // 记录操作日志
      await store.dispatch('operationLog/addLog', {
        adminId: store.getters['auth/adminInfo'].id,
        adminName: store.getters['auth/adminInfo'].name,
        action: '创建角色',
        module: '权限管理',
        target: `角色ID: ${newRole.id}`,
        details: `创建了新角色: ${newRole.name}`,
        ip: '192.168.1.100',
        status: 'success'
      })
    }
    
    roleDialogVisible.value = false
    refreshData()
  } catch (error) {
    if (error.message !== 'validate') {
      ElMessage.error('操作失败：' + error.message)
    }
  } finally {
    submitting.value = false
  }
}

// 生命周期
onMounted(() => {
  loadData()
})
</script>

<style scoped>
.role-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h1 {
  margin: 0;
  font-size: 24px;
  color: #ffffff;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.filter-card {
  margin-bottom: 20px;
}

.table-card {
  margin-bottom: 20px;
}

.permission-groups {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 10px;
}

.permission-group {
  margin-bottom: 15px;
}

.permission-group:last-child {
  margin-bottom: 0;
}

.module-title {
  font-weight: bold;
  color: #409eff;
  margin-bottom: 8px;
  padding-bottom: 4px;
  border-bottom: 1px solid #e4e7ed;
}

.permission-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.permission-desc {
  color: #909399;
  font-size: 12px;
  margin-left: 4px;
}

.permission-detail {
  .role-info {
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 1px solid #e4e7ed;
  }
  
  .role-info h3 {
    margin: 0 0 8px 0;
    color: #303133;
  }
  
  .role-info p {
    margin: 0;
    color: #606266;
  }
  
  .permission-list h4 {
    margin: 0 0 10px 0;
    color: #303133;
  }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .header-actions {
    width: 100%;
    justify-content: space-between;
  }
  
  .filter-card .el-form-item {
    width: 100%;
    margin-bottom: 10px;
  }
  
  .filter-card .el-form-item .el-input,
  .filter-card .el-form-item .el-select {
    width: 100%;
  }
}
</style>
